import React, { useContext, useEffect, useRef } from "react";
import { inject, observer } from "mobx-react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import AnalyseReactSelect from "./components/AnalyseReactSelect";
import IncidentsChart from "./components/IncidentsChart";
import IncidentsUnsupportedProjectMessage from "./components/IncidentsUnsupportedProjectMessage";
import BarChartIcon from "@material-ui/icons/BarChart";
import { AnalyseTabComponentProps } from "../../types";
import AnalyseStore from "store/analyse-store/analyseStore";
import GlobalContext from "GlobalContext";
import { analyseTheme } from "../../analyseTheme";
import {
  buildIncidentsSearch,
  parseIncidentsQueryParams,
  selectionFromStore,
} from "../../incidentsQuery";
import { buildModelDisplayLabel } from "tools/modelPredictions";

type Props = AnalyseTabComponentProps &
  RouteComponentProps & {
    analyseStore?: AnalyseStore;
  };

const IncidentsTab = ({
  analyseStore,
  tabLabel,
  TabIcon,
  params,
  history,
  location,
}: Props) => {
  const { appFetch } = useContext(GlobalContext) as any;
  const skipNextUrlSync = useRef(false);

  const selectedProjectId = analyseStore ? analyseStore.selectedProjectId : null;
  const selectedBatchId = analyseStore ? analyseStore.selectedBatchId : null;
  const selectedModelId = analyseStore ? analyseStore.selectedModelId : null;
  const selectedSecondaryModelId = analyseStore
    ? analyseStore.selectedSecondaryModelId
    : null;
  const selectedVideoId = analyseStore ? analyseStore.selectedVideoId : null;

  const syncUrlFromStore = () => {
    if (!analyseStore) return;
    skipNextUrlSync.current = true;
    const search = buildIncidentsSearch(
      location.search,
      selectionFromStore(analyseStore)
    );
    history.replace(`${location.pathname}${search}`);
  };

  useEffect(() => {
    if (!analyseStore) return;
    const fromUrl = parseIncidentsQueryParams(params);
    const fromStore = selectionFromStore(analyseStore);

    if (skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }

    const unchanged =
      fromUrl.modelId === fromStore.modelId &&
      fromUrl.secondaryModelId === fromStore.secondaryModelId &&
      fromUrl.projectId === fromStore.projectId &&
      fromUrl.batchId === fromStore.batchId &&
      fromUrl.mediaId === fromStore.mediaId;

    if (!unchanged) {
      analyseStore.applyParamsToSelection(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!analyseStore) return;
    if (analyseStore.projects.length === 0 && !analyseStore.isLoadingProjects) {
      analyseStore.fetchProjects(appFetch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!analyseStore || selectedProjectId == null) return;
    analyseStore.fetchBatches(appFetch, selectedProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  useEffect(() => {
    if (!analyseStore || selectedBatchId == null) return;
    analyseStore.fetchPredictionModels(appFetch, selectedBatchId);
    analyseStore.fetchVideos(appFetch, selectedBatchId);
    analyseStore.fetchBatchClasses(appFetch, selectedBatchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatchId]);

  useEffect(() => {
    if (!analyseStore) return;
    if (
      selectedVideoId == null ||
      selectedBatchId == null ||
      selectedProjectId == null ||
      analyseStore.isLoadingVideos ||
      analyseStore.isLoadingProjects
    ) {
      return;
    }
    if (!analyseStore.isSelectedProjectVideo) {
      return;
    }
    const selectedVideo = analyseStore.videos.find(
      (v) => v.id === selectedVideoId
    );
    if (!selectedVideo) return;
    if (selectedVideo.durationExactSec <= 0) {
      analyseStore.ensureVideoDuration(selectedVideoId);
      return;
    }
    analyseStore.fetchIncidentSeries(appFetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedModelId,
    selectedSecondaryModelId,
    selectedVideoId,
    selectedBatchId,
    selectedProjectId,
    analyseStore?.isLoadingVideos,
    analyseStore?.isLoadingProjects,
    analyseStore?.isSelectedProjectVideo,
    analyseStore?.videos.length,
    analyseStore?.videos.find((v) => v.id === selectedVideoId)
      ?.durationExactSec,
  ]);

  if (!analyseStore) {
    return null;
  }

  const {
    models,
    projects,
    batches,
    videos,
    isLoadingModels,
    isLoadingProjects,
    isLoadingBatches,
    isLoadingVideos,
    isLoadingSeries,
    errorMessage,
    currentSeries,
    currentVideo,
    selectedProjectLabelingType,
    isSelectedProjectVideo,
  } = analyseStore;

  const selectedModel = models.find((m) => m.id === selectedModelId);
  const selectedSecondaryModel = models.find(
    (m) => m.id === selectedSecondaryModelId
  );
  const selectedModelLabel = selectedModel
    ? buildModelDisplayLabel(selectedModel.name, selectedModel.version)
    : null;
  const selectedSecondaryModelLabel = selectedSecondaryModel
    ? buildModelDisplayLabel(
        selectedSecondaryModel.name,
        selectedSecondaryModel.version
      )
    : null;
  const selectedModelCount =
    (selectedModelLabel ? 1 : 0) + (selectedSecondaryModelLabel ? 1 : 0);

  const secondaryModelOptions = models
    .filter((m) => m.id !== selectedModelId)
    .map((m) => ({
      value: m.id,
      label: buildModelDisplayLabel(m.name, m.version),
    }));

  const HeaderIcon = TabIcon;
  const hasMediaSelection =
    selectedProjectId != null &&
    selectedBatchId != null &&
    selectedVideoId != null;
  const isEvaluatingProjectType =
    hasMediaSelection && (isLoadingProjects || isLoadingVideos);
  const showUnsupportedProject =
    hasMediaSelection &&
    !isLoadingProjects &&
    !isLoadingVideos &&
    !isSelectedProjectVideo;
  const showChart =
    isSelectedProjectVideo &&
    currentVideo != null &&
    currentVideo.durationExactSec > 0 &&
    currentSeries != null &&
    !isLoadingSeries;
  const showPlaceholder = !showChart && !showUnsupportedProject;

  const hasSelection =
    selectedProjectId != null ||
    selectedBatchId != null ||
    selectedModelId != null ||
    selectedVideoId != null;

  const handleResetSelection = () => {
    analyseStore.setSelectedProject(null);
    syncUrlFromStore();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, minHeight: 360 }}>
      <div
        style={{
          display: "flex",
          width: "100%",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        {HeaderIcon && tabLabel && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginRight: "auto",
              flexShrink: 0,
              color: analyseTheme.textPrimary,
            }}
          >
            {/* <HeaderIcon
              style={{ fontSize: 26, color: analyseTheme.secondaryLight }}
            /> */}
            <Typography
              variant="h6"
              style={{ fontWeight: 600, fontSize: "1.125rem", margin: 0 }}
            >
              {tabLabel}
            </Typography>
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flex: "0 1 auto",
            minWidth: 0,
          }}
        >
          <Button
            color="secondary"
            disabled={!hasSelection}
            onClick={handleResetSelection}
            style={{
              textTransform: "none",
              fontSize: "0.8125rem",
              fontWeight: 500,
              minWidth: "auto",
              padding: "8px 4px",
              marginBottom: 1,
              textDecoration: "underline",
              lineHeight: 1.2,
              flexShrink: 0,
            }}
          >
            Réinitialiser
          </Button>

          <AnalyseReactSelect
            id="analyse-project-select"
            label="Projet"
            value={selectedProjectId == null ? "" : String(selectedProjectId)}            
            disabled={isLoadingProjects}
            loading={isLoadingProjects}
            options={projects.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
            onChange={(value) => {
              analyseStore.setSelectedProject(value === "" ? null : value);
              syncUrlFromStore();
            }}
          />

          <AnalyseReactSelect
            id="analyse-batch-select"
            label="Lot"
            value={selectedBatchId == null ? "" : String(selectedBatchId)}            
            disabled={selectedProjectId == null || isLoadingBatches}
            loading={isLoadingBatches}
            emptyLabel={selectedProjectId == null ? "Lot" : undefined}
            options={batches.map((b) => ({ value: b.id, label: b.name }))}
            onChange={(value) => {
              analyseStore.setSelectedBatch(
                value === "" ? null : Number(value)
              );
              syncUrlFromStore();
            }}
          />

          <AnalyseReactSelect
            id="analyse-model-select"
            label="Modele"
            value={selectedModelId || ""}            
            disabled={selectedBatchId == null || isLoadingModels}
            loading={isLoadingModels}
            emptyLabel={selectedBatchId == null ? "Modele" : undefined}
            options={models.map((m) => ({
              value: m.id,
              label: buildModelDisplayLabel(m.name, m.version),
            }))}
            onChange={(value) => {
              analyseStore.setSelectedModel(value === "" ? null : value);
              syncUrlFromStore();
            }}
          />

          <AnalyseReactSelect
            id="analyse-secondary-model-select"
            label="Modele 2"
            value={selectedSecondaryModelId || ""}            
            disabled={
              selectedBatchId == null ||
              selectedModelId == null ||
              isLoadingModels
            }
            loading={isLoadingModels}
            emptyLabel={
              selectedModelId == null ? "Modele 2" : undefined
            }
            options={secondaryModelOptions}
            onChange={(value) => {
              analyseStore.setSelectedSecondaryModel(
                value === "" ? null : value
              );
              syncUrlFromStore();
            }}
          />

          <AnalyseReactSelect
            id="analyse-video-select"
            label="Media"
            value={selectedVideoId == null ? "" : String(selectedVideoId)}
            // flex="1 1 150px"
            // maxWidth={150}
            disabled={selectedBatchId == null || isLoadingVideos}
            loading={isLoadingVideos}
            emptyLabel={selectedBatchId == null ? "Media" : undefined}
            options={videos.map((v) => ({ value: v.id, label: v.name }))}
            onChange={(value) => {
              analyseStore.setSelectedVideo(value === "" ? null : value);
              syncUrlFromStore();
            }}
          />
        </div>
      </div>

      {errorMessage && (
        <Typography variant="caption" color="error">
          {errorMessage}
        </Typography>
      )}

      <div
        style={{
          flex: 1,
          backgroundColor: analyseTheme.surface,
          minHeight: 300,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {showUnsupportedProject && (
          <IncidentsUnsupportedProjectMessage
            labelingType={selectedProjectLabelingType}
          />
        )}
        {showPlaceholder && (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              color: analyseTheme.textSecondary,
              minHeight: 260,
            }}
          >
            <BarChartIcon
              style={{
                fontSize: 128,
                opacity: 0.45,
                color: analyseTheme.secondaryLight,
              }}
            />
            <Typography variant="body2" color="textSecondary">
              {isLoadingSeries || isEvaluatingProjectType
                ? "Chargement de la comparaison des incidents..."
                : "Selectionnez un projet, un lot et un media pour afficher la comparaison des incidents."}
            </Typography>
          </div>
        )}
        {showChart && currentVideo && currentSeries && (
          <IncidentsChart
            classes={currentSeries.classes}
            labelerIntervals={currentSeries.labelerIntervals}
            modelIntervals={currentSeries.modelIntervals}
            secondaryModelIntervals={currentSeries.secondaryModelIntervals}
            durationSec={currentVideo.durationSec}
            durationExactSec={currentVideo.durationExactSec}
            modelLabel={selectedModelLabel}
            secondaryModelLabel={selectedSecondaryModelLabel}
            selectedModelCount={selectedModelCount}
            mediaName={currentVideo.name}
            isLoadingVideos={isLoadingVideos || isLoadingSeries}
          />
        )}
      </div>
    </div>
  );
};

export default withRouter(inject("analyseStore")(observer(IncidentsTab)));
